<script lang="ts" setup>
import { ref, computed } from "vue";
import { useRoute, useRouter } from "vue-router";
import {
  Tabs,
  Breadcrumbs,
  DocCopyDialog,
  Tooltip,
  FlashMessage,
} from "@/components";
import { useStore } from "vuex";
import { useDocumentEditorShellQuery } from "@/graphql/operations";
import { MimeType } from "@/util";
import { useDocumentDownload } from "@/composables/useDocumentDownload";
import DocumentDeleteConfirmation from "./DocumentDeleteConfirmation.vue";
import { hasPresentationAnalyticsAccess } from "./presentation_analytics/presentationAnalytics";

const store = useStore();

const route = useRoute();
const router = useRouter();
const docId = computed(() => route.params.documentId as string);

const { fetching, data, error, stale } = useDocumentEditorShellQuery({
  variables: computed(() => ({
    id: docId.value,
  })),
  requestPolicy: "network-only",
});

const document = computed(() => {
  const loadedDocument = data.value?.document;

  return loadedDocument?.id === docId.value ? loadedDocument : undefined;
});

const canViewPresentationAnalytics = computed(() =>
  hasPresentationAnalyticsAccess({
    hasCurrentAccessResult: !fetching.value && !stale.value && !error.value,
    hasPresentationAnalytics: store.getters.user.hasPresentationAnalytics(),
    mayAdministrateRoot: Boolean(
      document.value?.rootWorkspace?.myPrivileges?.mayAdministrate,
    ),
    deletedAt: document.value?.deletedAt,
    mimeType: document.value?.mimeType,
    numNormalPages: document.value?.numNormalPages,
  }),
);

const tabItems = computed(() => {
  const items = [
    {
      title: "Properties",
      routeParams: { name: "DocumentProperties", params: route.params },
    },
  ];

  if (!MimeType.isVideo(document.value?.mimeType || "")) {
    items.push({
      title: "Studio",
      routeParams: { name: "MagicPagesStudio", params: route.params },
    });
  }

  if (canViewPresentationAnalytics.value) {
    items.push({
      title: "Analytics",
      routeParams: {
        name: "DocumentPresentationAnalytics",
        params: route.params,
      },
    });
  }

  return items;
});

const breadcrumbItems = computed(() => [
  {
    text: store.getters.currentWorkspace?.name,
    href: router.resolve({
      name: "Workspace",
      params: { workspaceId: route.params.workspaceId as string },
    }).fullPath,
  },
  {
    text: document.value?.displayTitle,
  },
]);

const projectorLink = computed(
  () =>
    router.resolve({
      name: "StackedProjector",
      params: { workspaceId: route.params.workspaceId },
      query: { fid: document.value?.firestoreId },
    }).fullPath,
);

enum CopyAction {
  MOVE = "MOVE",
  COPY = "COPY",
}

const copyDialogActive = ref<boolean>(false);
const copyAction = ref<CopyAction | null>(null);

const showCopyDialog = (action: CopyAction) => {
  copyAction.value = action;
  copyDialogActive.value = true;
};

const closeCopyDialog = () => {
  copyDialogActive.value = false;
};

const flashMessageActive = ref<boolean>(false);
const displayFlashMessage = () => {
  flashMessageActive.value = true;
  window.setTimeout(() => (flashMessageActive.value = false), 1500);
};

const deleteConfirmationActive = ref<boolean>(false);
const showDeleteConfirmation = () => {
  deleteConfirmationActive.value = true;
};

const closeDeleteConfirmation = () => {
  deleteConfirmationActive.value = false;
};

const { download, downloading, downloadError } = useDocumentDownload();
const canDownload = computed(() =>
  Boolean(
    document.value &&
      (document.value.allowUserDownload ||
        document.value.rootWorkspace?.myPrivileges?.mayAdministrate),
  ),
);
</script>

<template>
  <div class="pane page_content">
    <FlashMessage
      v-if="flashMessageActive"
      :state="'success'"
      :message="'Erfolgreich gespeichert'"
    />

    <FlashMessage
      v-if="downloadError"
      state="error"
      :message="$t('pvDocEditor.downloadError')"
    />

    <div v-if="fetching || stale"></div>

    <template v-else>
      <div class="title_container">
        <div class="left">
          <Breadcrumbs :items="breadcrumbItems" />
          <span
            v-if="document?.documentExpiration?.expiredAt"
            class="badge error"
          >
            {{ $t("pvCardDoc.badge.expired") }}
          </span>
        </div>

        <div class="buttons">
          <Tooltip>
            <template v-slot>
              <a :href="projectorLink" target="_blank">
                <span class="material-symbols-outlined filled">
                  visibility
                </span>
              </a>
            </template>
            <template #tooltip>{{ $t("pvDocEditor.preview") }}</template>
          </Tooltip>

          <Tooltip v-if="canDownload">
            <template v-slot>
              <button
                type="button"
                class="icon_action"
                :aria-label="$t('pvDocEditor.download')"
                :disabled="downloading"
                @click="download(document!.id)"
              >
                <span class="material-symbols-outlined filled"> download </span>
              </button>
            </template>
            <template #tooltip>{{ $t("pvDocEditor.download") }}</template>
          </Tooltip>

          <Tooltip v-if="false">
            <template v-slot>
              <span
                class="material-symbols-outlined filled"
                @click="showCopyDialog(CopyAction.COPY)"
                v-if="
                  store.getters.currentWorkspace?.root.myPrivileges
                    .mayAdministrate && store.getters.workspaces.length > 1
                "
              >
                file_copy
              </span>
            </template>
            <template #tooltip>{{ $t("pvDocEditor.copy") }}</template>
          </Tooltip>

          <Tooltip>
            <template v-slot>
              <button
                type="button"
                class="icon_action"
                :aria-label="$t('pvDocEditor.move')"
                @click="showCopyDialog(CopyAction.MOVE)"
                v-if="
                  store.getters.currentWorkspace?.root.myPrivileges
                    .mayAdministrate && store.getters.workspaces.length > 1
                "
              >
                <span class="material-symbols-outlined filled">
                  drive_file_move
                </span>
              </button>
            </template>
            <template #tooltip>{{ $t("pvDocEditor.move") }}</template>
          </Tooltip>

          <Tooltip>
            <template v-slot>
              <button
                type="button"
                class="icon_action"
                :aria-label="$t('pvDocEditor.delete')"
                @click="showDeleteConfirmation"
                v-if="
                  store.getters.currentWorkspace?.root.myPrivileges
                    .mayAdministrate
                "
                data-test="delete-doc-ctrl"
              >
                <span class="material-symbols-outlined filled dangerous">
                  delete
                </span>
              </button>
            </template>
            <template #tooltip>{{ $t("pvDocEditor.delete") }}</template>
          </Tooltip>
        </div>
      </div>

      <Tabs :items="tabItems"></Tabs>

      <router-view></router-view>
    </template>

    <doc-copy-dialog
      v-if="copyDialogActive"
      :active="copyDialogActive"
      :doc="document!"
      :action="copyAction as string"
      @close="closeCopyDialog"
      @success="displayFlashMessage"
    />

    <document-delete-confirmation
      v-if="deleteConfirmationActive"
      :docId="docId"
      @close="closeDeleteConfirmation"
    />
  </div>
</template>

<style lang="scss" scoped>
.page_content {
  height: 100%;
  position: relative;
}

.title_container {
  display: flex;
  align-items: center;
  margin-bottom: 2em;
}

.title_container .left {
  flex: 1;
  display: flex;
  align-items: center;
}

.buttons {
  color: var(--classic-text, #4a4a4a);
}

.buttons a {
  color: inherit;
}

.buttons .icon_action {
  appearance: none;
  background: transparent;
  border: 0;
  color: inherit;
  font: inherit;
  line-height: inherit;
  margin: 0;
  padding: 0;
}

.buttons .material-symbols-outlined {
  font-size: 1.1em;
  border: 1px solid var(--classic-border-subtle, #ebebeb);
  border-radius: 50%;
  padding: 0.58em;
  line-height: 1;
  margin-left: 0.5em;
}

.buttons .material-symbols-outlined:hover {
  cursor: pointer;
  background: var(--classic-surface-inset, #f4f4f4);
}

.badge {
  background-color: var(--classic-surface-raised, #f9f9f9);
  font-size: 0.8em;
  padding: 0 10px;
  line-height: 1.5em;
  height: 1.5em;
  font-weight: 500;
  border-radius: 4%;
  margin-left: 0.5em;
}

.badge.error {
  background: $color-error;
  color: #fff;
}
</style>
